/**
 * Google Places Address Parser Utility
 * Pure functions for parsing Google Places API responses into structured address data
 *
 * This module is completely decoupled from React and can be unit tested easily.
 */

import {
  ADDRESS_COMPONENT_TYPES,
  EMPTY_ADDRESS,
  DEBUG_MODE,
} from '../constants/googlePlaces';
import logger from './logger';

/**
 * Google Places Address Parser
 * Provides static methods for parsing Google Maps Place objects
 */
export class GooglePlacesParser {
  /**
   * Parse Google Place addressComponents into structured address object
   *
   * @param {Array} addressComponents - Array of Google Place addressComponent objects
   * @returns {Object} Parsed address with streetNumber, route, city, state, zip, country
   *
   * @example
   * const parsed = GooglePlacesParser.parseAddressComponents(place.addressComponents);
   * logger.debug(parsed.city); // "Tallahassee"
   */
  static parseAddressComponents(addressComponents) {
    // Return empty address if no components provided
    if (!addressComponents || !Array.isArray(addressComponents)) {
      if (DEBUG_MODE) {
        logger.warn('[GooglePlacesParser] No address components provided');
      }
      return { ...EMPTY_ADDRESS };
    }

    const result = { ...EMPTY_ADDRESS };

    if (DEBUG_MODE) {
      logger.debug('[GooglePlacesParser] Parsing address components:', addressComponents);
    }

    addressComponents.forEach(component => {
      if (DEBUG_MODE) {
        logger.debug('[GooglePlacesParser] Component:', {
          types: component.types,
          // Show both legacy and new API field names for debugging
          long_name: component.long_name,
          short_name: component.short_name,
          longText: component.longText,
          shortText: component.shortText,
        });
      }

      const types = component.types || [];

      // Extract street number
      if (types.includes(ADDRESS_COMPONENT_TYPES.STREET_NUMBER)) {
        result.streetNumber = this._extractText(component);
        if (DEBUG_MODE) {
          logger.debug('[GooglePlacesParser] ✅ Found street_number:', result.streetNumber);
        }
      }

      // Extract route (street name)
      if (types.includes(ADDRESS_COMPONENT_TYPES.ROUTE)) {
        result.route = this._extractText(component);
        if (DEBUG_MODE) {
          logger.debug('[GooglePlacesParser] ✅ Found route:', result.route);
        }
      }

      // Extract city
      if (types.includes(ADDRESS_COMPONENT_TYPES.LOCALITY)) {
        result.city = this._extractText(component);
        if (DEBUG_MODE) {
          logger.debug('[GooglePlacesParser] ✅ Found locality (city):', result.city);
        }
      }

      // Extract state (both full name and code)
      if (types.includes(ADDRESS_COMPONENT_TYPES.STATE)) {
        // Legacy API: long_name / short_name, New API: longText / shortText
        result.state = component.long_name || component.longText || '';
        result.stateCode = component.short_name || component.shortText || '';
        if (DEBUG_MODE) {
          logger.debug(
            '[GooglePlacesParser] ✅ Found state:',
            result.state,
            result.stateCode
          );
        }
      }

      // Extract ZIP code
      if (types.includes(ADDRESS_COMPONENT_TYPES.POSTAL_CODE)) {
        result.zip = this._extractText(component);
        if (DEBUG_MODE) {
          logger.debug('[GooglePlacesParser] ✅ Found postal_code:', result.zip);
        }
      }

      // Extract country (both full name and code)
      if (types.includes(ADDRESS_COMPONENT_TYPES.COUNTRY)) {
        // Legacy API: long_name / short_name, New API: longText / shortText
        result.country = component.long_name || component.longText || '';
        result.countryCode = component.short_name || component.shortText || '';
        if (DEBUG_MODE) {
          logger.debug(
            '[GooglePlacesParser] ✅ Found country:',
            result.country,
            result.countryCode
          );
        }
      }
    });

    if (DEBUG_MODE) {
      logger.debug('[GooglePlacesParser] Final parsed result:', result);
    }

    return result;
  }

  /**
   * Extract text from address component
   * Supports both Legacy API (long_name/short_name) and New API (longText/shortText)
   *
   * @private
   * @param {Object} component - Google Place addressComponent object
   * @returns {string} Extracted text or empty string
   */
  static _extractText(component) {
    // Legacy API uses long_name/short_name (snake_case)
    // New API uses longText/shortText (camelCase)
    return component.long_name || component.longText || component.short_name || component.shortText || '';
  }

  /**
   * Build full street address from parsed components
   * Follows Shopify pattern: ONLY street number + route (no city, state, zip)
   *
   * @param {Object} parsedAddress - Result from parseAddressComponents()
   * @returns {string} Full street address (e.g., "222 South Copeland Street")
   *
   * @example
   * const address = GooglePlacesParser.buildStreetAddress(parsed);
   * // Returns: "222 South Copeland Street"
   */
  static buildStreetAddress(parsedAddress) {
    if (!parsedAddress) return '';

    const { streetNumber, route } = parsedAddress;

    // Build street address: number + street name
    const fullAddress = `${streetNumber} ${route}`.trim();

    if (DEBUG_MODE) {
      logger.debug('[GooglePlacesParser] Built street address:', fullAddress);

      // Warn if no street address could be built
      if (!fullAddress) {
        logger.warn(
          '[GooglePlacesParser] ⚠️ No street address found. Selected location may be too general (city/region).'
        );
      }
    }

    return fullAddress;
  }

  /**
   * Parse a complete Google Place object into structured address
   * Convenience method that combines parsing and street address building
   * Supports both Legacy API (address_components) and New API (addressComponents)
   *
   * @param {Object} place - Google Maps Place object
   * @returns {Object} Object with parsedAddress and streetAddress
   *
   * @example
   * const result = GooglePlacesParser.parsePlace(place);
   * logger.debug(result.parsedAddress.city); // "Tallahassee"
   * logger.debug(result.streetAddress); // "222 South Copeland Street"
   */
  static parsePlace(place) {
    // Support both Legacy API (address_components) and New API (addressComponents)
    const addressComponents = place?.address_components || place?.addressComponents;

    if (!place || !addressComponents) {
      if (DEBUG_MODE) {
        logger.warn('[GooglePlacesParser] Invalid place object:', place);
      }
      return {
        parsedAddress: { ...EMPTY_ADDRESS },
        streetAddress: '',
      };
    }

    const parsedAddress = this.parseAddressComponents(addressComponents);
    const streetAddress = this.buildStreetAddress(parsedAddress);

    return {
      parsedAddress,
      streetAddress,
    };
  }

  /**
   * Validate that parsed address has minimum required fields for checkout
   * Required: city, state, zip (street is optional - user can enter manually)
   *
   * @param {Object} parsedAddress - Result from parseAddressComponents()
   * @returns {Object} { isValid: boolean, missingFields: string[] }
   *
   * @example
   * const validation = GooglePlacesParser.validateAddress(parsed);
   * if (!validation.isValid) {
   *   logger.debug('Missing:', validation.missingFields);
   * }
   */
  static validateAddress(parsedAddress) {
    const missingFields = [];

    if (!parsedAddress.city) missingFields.push('city');
    if (!parsedAddress.state) missingFields.push('state');
    if (!parsedAddress.zip) missingFields.push('zip');

    const isValid = missingFields.length === 0;

    if (DEBUG_MODE && !isValid) {
      logger.warn(
        '[GooglePlacesParser] Address validation failed. Missing fields:',
        missingFields
      );
    }

    return {
      isValid,
      missingFields,
    };
  }

  /**
   * Check if the selected place is too general (city/state only, no street)
   * Useful for showing warnings to users
   *
   * @param {Object} parsedAddress - Result from parseAddressComponents()
   * @returns {boolean} True if location is generic (no street address)
   */
  static isGenericLocation(parsedAddress) {
    const hasStreet = !!(parsedAddress.streetNumber || parsedAddress.route);
    return !hasStreet;
  }
}

export default GooglePlacesParser;
